import { EntityNode } from "./EntityNode";
import { ResizeNode } from "../Nodes/ResizeNode";
import { RotationNode } from "../Nodes/RotationNode";
import { DimensionNode } from "../Nodes/DimensionNode";
import { GameObjectManager } from "../GameObjectManager";
import { Utils } from "../../utils/Utils";
import { ActionStateEnum } from "../../state/actionState/ActionState";
import { PlanManager } from "../../../../cocosPlan";
import { DoubleQuarterStaircase, Events, eventsManager, math, SceneConstants, Staircase } from "@rhinov/savane-js";

declare var cc: any;

export class StaircaseNode extends EntityNode {

    _NOT_VALID_OVERLAP_COLOR = new cc.Color(255, 125, 141, 255);
    static HALF_RESIZE_CURSOR_SIZE: number = 5;

    constructor(techElement: Staircase) {
        super(techElement);
        this.isValid = true;
        this.techElem = techElement;
        this.rotationmenu = new RotationNode(techElement);
        this.resizeMenu = new ResizeNode(techElement, undefined as any, undefined as any);
        this.addChild(this.rotationmenu);
        this.addChild(this.resizeMenu, 8);

        this.createDimensions();

        this.hideStairAxo = eventsManager.instance.addListener(Events.HIDE_AXO, (_event: any) => {
            if (this.techElem.hideInAxo) {
                this.needRedraw = true;
                this.setVisible(!PlanManager.getInstance()._hideAxo);
            }
        });
    }

    updateDimensionNode(node: any, begin: any, end: any, normal: any, dist: number) {
        var cote_begin = new cc.p(begin.x + dist * normal.x, begin.y + dist * normal.y);
        var cote_end = new cc.p(end.x + dist * normal.x, end.y + dist * normal.y);
        var cote_pos = new cc.p((cote_begin.x + cote_end.x) / 2, (cote_begin.y + cote_end.y) / 2);
        node.setPosition(cote_pos);
        var calcDist = Math.sqrt((end.x - begin.x) * (end.x - begin.x) + (end.y - begin.y) * (end.y - begin.y));
        node.setDimensionValue(calcDist, PlanManager.getInstance().getZoomScale());

        var angle = Math.atan2(end.x - begin.x, end.y - begin.y);
        var cocosAngle = (angle + Math.PI * 0.5) * 180 / Math.PI;
        if (cocosAngle < -90.0)
            cocosAngle += 180;
        if (cocosAngle >= 90.0)
            cocosAngle -= 180;

        var parentAngle = (this.techElem.angle * 180) / Math.PI;
        if (parentAngle < -90.0)
            cocosAngle += 180;
        if (parentAngle > 90.0)
            cocosAngle -= 180;

        node.setRotation(cocosAngle);
    }

    updateDimensions() {
        var norm1 = new cc.p(0, -1);
        this.updateDimensionNode(this.dim1, new cc.p((this.techElem.length / 2) / 10, -(this.techElem.width / 2) / 10), new cc.p(-(this.techElem.length / 2) / 10, -(this.techElem.width / 2) / 10), norm1, Utils.DIMENSION_DIST * 1.5);

        var norm2 = new cc.p(1, 0);
        this.updateDimensionNode(this.dim2, new cc.p((this.techElem.length / 2) / 10, -(this.techElem.width / 2) / 10), new cc.p((this.techElem.length / 2) / 10, (this.techElem.width / 2) / 10), norm2, Utils.DIMENSION_DIST * 1.5);
    }

    createDimensions() {
        this.dim1 = new DimensionNode(undefined);
        this.dim1.setName('techElementDimension1');
        this.dim1.setTag(this.techElem.id);
        this.dim1.setVisible(false);
        this.addChild(this.dim1, 8);

        this.dim2 = new DimensionNode(undefined);
        this.dim1.setName('techElementDimension2');
        this.dim2.setTag(this.techElem.id);
        this.dim2.setVisible(false);
        this.addChild(this.dim2, 8);
    }

    toRotate(position: math.vec3): boolean {
        return this.rotationmenu.toRotate(position);
    }

    drawDashedLine(origin: any, destination: any, color: any) {
        var delta = cc.pSub(destination, origin);
        var dist = cc.pDistance(origin, destination);
        var dashLength = Math.max(6, dist / 20);
        var x = delta.x / dist * dashLength;
        var y = delta.y / dist * dashLength;
        var linePercentage = 0.3;

        var p1 = origin;
        for (var i = 1;
            i < dist / dashLength * linePercentage;
            i++
        ) {
            p1 = new cc.p(origin.x + (x / linePercentage * i), origin.y + (y / linePercentage * i));
            var p2 = new cc.p(p1.x + x, p1.y + y);
            this.drawSegment(p1, p2, 3, color);
        }
    }

    drawArc(origin: any, radius: any, angle: any, segments: any, dotted: any, mainColor: any) {
        var i;
        var start = cc.pAdd(origin, new cc.p(radius, 0));
        var angle_step = angle / segments;
        var circle = [];
        var yellow = new cc.color('F5D231');

        if ((this.techElem.floor !== null) && (this.techElem.floor.id !== PlanManager.getInstance().world.currentScene!.currentFloor!.id)) {
            yellow.a = PlanManager.getInstance().opacityFloors;
        }

        circle.push(start);
        for (i = 0; i <= segments; i++) {
            var rads = angle_step * i;
            var x = origin.x + radius * Math.cos(rads);
            var y = origin.y + radius * Math.sin(rads);
            circle.push(new cc.p(x, y));
        }
        var dotStep = 0;
        var dot = false;
        var color = new cc.color('F5D231');

        if ((this.techElem.floor !== null) && (this.techElem.floor.id !== PlanManager.getInstance().world.currentScene!.currentFloor!.id)) {
            color.a = PlanManager.getInstance().opacityFloors;
        }

        for (i = 0; i < circle.length; i += 1) {
            if (i > 0) {

                if (dotStep > segments / 20) {
                    if (dot === false) {
                        dot = true;
                        if (dotted) {
                            color = mainColor;
                        }
                    } else {
                        dot = false;
                        color = yellow;
                    }
                    dotStep = 0;
                }
                this.drawSegment(circle[i - 1], circle[i], 2, color);
                dotStep++;
            }
        }
    }

    getOverLapColor(isValid: boolean): {border: any, plain: any} {
        var colorItem = null;
        if (isValid) {
            if (this._isSelected) {
                colorItem = {
                    plain: new cc.color('#fbe800'),
                    border: new cc.color('#fbe800')
                };
            }
            else {
                if (this._isHovered) {
                    colorItem = {
                        plain: new cc.color(185, 185, 185),
                        border: new cc.Color(66, 80, 94, 255)
                    };
                } else {
                    colorItem = {
                        plain: new cc.color(255, 255, 255),
                        border: new cc.Color(66, 80, 94, 255)
                    };
                }
            }
        } else {
            colorItem = {
                plain: this._NOT_VALID_OVERLAP_COLOR,
                border: this._NOT_VALID_OVERLAP_COLOR
            };
        }
        if (PlanManager.getInstance().highlightMode) {
            colorItem.plain.a = 75;
        }

        if ((this.techElem.floor !== null) && (this.techElem.floor.id !== PlanManager.getInstance().world.currentScene!.currentFloor!.id)) {
            colorItem.plain.a = PlanManager.getInstance().opacityFloors;
            colorItem.border.a = PlanManager.getInstance().opacityFloors;
        }

        return colorItem;
    }

    updateModel(techElement: Staircase, isValid: boolean) {
        var planManager = PlanManager.getInstance();

        this.clear();
        if (isValid != undefined) {
            this.isValid = isValid;
        }

        // In decoration state, always hide rotation and resize nodes
        if (planManager.getAction() !== ActionStateEnum.idleDecoration) {
            this.rotationmenu.setVisible(this._isSelected);
            this.rotationmenu.update();
            this.resizeMenu.setVisible(this._isSelected);
            this.resizeMenu.update();
        }
        else {
            this.rotationmenu.setVisible(false);
            this.resizeMenu.setVisible(false);
        }

        this.updateDimensions();

        this.dim1.setVisible(this._isSelected);
        this.dim1.update();
        this.dim2.setVisible(this._isSelected)
        this.dim2.update();

        //  var topleft = new cc.p(0,0);
        //  var botRight = new cc.p(50,50);
        //var color = this.isValid ? (this._isSelected ? new cc.color(190, 190, 190) : new cc.color(155, 155, 155)) : new cc.color(255, 125, 141, 255);

        var color = {border: undefined, plain: undefined};
        if (isValid != undefined) {
            color = this.getOverLapColor(isValid);
        }
        else {
            color = this.getOverLapColor(true);
        }

        var decalRight = null;
        var decalLeft = null;
        var yellow = new cc.color('F5D231');
        if ((this.techElem.floor !== null) && (this.techElem.floor.id !== PlanManager.getInstance().world.currentScene!.currentFloor!.id)) {
            yellow.a = PlanManager.getInstance().opacityFloors;
        }

        if (techElement.objectId === SceneConstants.StaircaseType.straight) {
            var topleft = new cc.p(-techElement.length / 20, -techElement.width / 20);
            var botRight = new cc.p(techElement.length / 20, techElement.width / 20);

            var dirBegin = new cc.p(0, -techElement.width / 20 + techElement.width / 20 * 0.1);
            var dirEnd = new cc.p(0, botRight.y - botRight.y * 0.1);

            this.drawRect(topleft, botRight, color.plain, 2, color.border);
            this.drawSegment(dirBegin, dirEnd, 2, yellow);

            if (!techElement.isGoingDown) {
                this.drawDashedLine(dirBegin, dirEnd, color.plain);
            }
            if (techElement.way === false) {
                decalRight = new cc.p(dirBegin.x + dirBegin.y * 0.10, dirBegin.y - dirBegin.y * 0.1);
                decalLeft = new cc.p(dirBegin.x - dirBegin.y * 0.10, dirBegin.y - dirBegin.y * 0.1);

                this.drawSegment(dirBegin, decalRight, 2, yellow);
                this.drawSegment(dirBegin, decalLeft, 2, yellow);
            } else {
                decalRight = new cc.p(dirEnd.x + dirEnd.y * 0.10, dirEnd.y + dirBegin.y * 0.1);
                decalLeft = new cc.p(dirEnd.x - dirEnd.y * 0.10, dirEnd.y + dirBegin.y * 0.1);
                this.drawSegment(dirEnd, decalRight, 2, yellow);
                this.drawSegment(dirEnd, decalLeft, 2, yellow);
            }
        } else if (techElement.shapeType === SceneConstants.ShapeType.ellipse) {
            var dist = techElement.width / 10;//Math.sqrt((techElement.width / 10) * (techElement.width / 10) + (techElement.length / 10) * (techElement.length / 10));
            this.drawDot(cc.p(0, 0), dist * 0.5, color.border);
            this.drawDot(cc.p(0, 0), dist * 0.5 - 2, color.plain);

            if (!techElement.way) {
                var left = new cc.p(-dist * 0.3, 0);
                decalRight = new cc.p(left.x + left.x * 0.10, left.y + left.x * 0.1);
                decalLeft = new cc.p(left.x - left.x * 0.10, left.y + left.x * 0.1);

                this.drawArc(cc.p(0, 0), dist * 0.3, -3.14, 100, !techElement.isGoingDown, color.plain);

                this.drawSegment(left, decalRight, 2, yellow);
                this.drawSegment(left, decalLeft, 2, yellow);
            }
            else {
                var left = new cc.p(-dist * 0.3, 0);
                decalRight = new cc.p(left.x + left.x * 0.10, left.y - left.x * 0.1);
                decalLeft = new cc.p(left.x - left.x * 0.10, left.y - left.x * 0.1);

                this.drawArc(cc.p(0, 0), dist * 0.3, 3.14, 100, !techElement.isGoingDown, color.plain);

                this.drawSegment(left, decalRight, 2, yellow);
                this.drawSegment(left, decalLeft, 2, yellow);
            }

            this.drawSegment(cc.p(0, 0), cc.p(dist * 0.5 - 2, 0), 1, color.border);
        } else {
            var array = [];
            var ligneArray = [];
            var partlength = (techElement as DoubleQuarterStaircase).stepWidth / 10;
            var length = techElement.length / 20;
            var width = techElement.width / 20;
            var mWidth = (techElement as DoubleQuarterStaircase).modifiedWidth / 10;
            if (techElement.objectId === SceneConstants.StaircaseType.doubleQuarterTurn) {
                array.push(new cc.p(length, -width));
                if (techElement.way === true) {
                    array.push(new cc.p(length, -width + mWidth));
                    array.push(new cc.p(length - partlength, -width + mWidth));
                } else {
                    array.push(new cc.p(length, width));
                    array.push(new cc.p(length - partlength, width));
                }
                array.push(new cc.p(length - partlength, -width + partlength));
                array.push(new cc.p(-length + partlength, -width + partlength));
                if (techElement.way === true) {
                    array.push(new cc.p(-length + partlength, width));
                    array.push(new cc.p(-length, width));
                } else {
                    array.push(new cc.p(-length + partlength, -width + mWidth));
                    array.push(new cc.p(-length, -width + mWidth));
                }
                array.push(new cc.p(-length, -width));
                if (techElement.way === true) {
                    ligneArray.push(new cc.p(-length + partlength / 2, width - width * 0.1));
                } else {
                    ligneArray.push(new cc.p(-length + partlength / 2, -width + mWidth - width * 0.1));
                }
                ligneArray.push(new cc.p(-length + partlength / 2, -width + partlength / 2));
                ligneArray.push(new cc.p(length - partlength / 2, -width + partlength / 2));
                if (techElement.way === true) {
                    ligneArray.push(new cc.p(length - partlength / 2, -width + mWidth - width * 0.1));
                } else {
                    ligneArray.push(new cc.p(length - partlength / 2, width - width * 0.1));
                }
            }
            if (techElement.objectId === SceneConstants.StaircaseType.quarterTurn) {
                array.push(new cc.p(length, -width));
                array.push(new cc.p(length, width));
                array.push(new cc.p(length - partlength, width));
                array.push(new cc.p(length - partlength, -width + partlength));
                array.push(new cc.p(-length, -width + partlength));
                array.push(new cc.p(-length, -width));
                ligneArray.push(new cc.p(-length + length * 0.1, -width + partlength / 2));
                ligneArray.push(new cc.p(length - partlength / 2, -width + partlength / 2));
                ligneArray.push(new cc.p(length - partlength / 2, width - width * 0.1));

            }

            var borderthickness = 1;
            if (borderthickness * planManager.getZoomScale() < 1 || borderthickness * planManager.getZoomScale() > 1) {
                borderthickness /= planManager.getZoomScale();
            }

            this.drawPoly(array, color.plain, 2, color.border);
            for (var i = 0; i < ligneArray.length; ++i) {
                if (i !== 0) {
                    this.drawSegment(ligneArray[i - 1], ligneArray[i], 2, yellow);

                    if (!techElement.isGoingDown) {
                        if (techElement.way === true && i === ligneArray.length - 1) {

                            this.drawDashedLine(ligneArray[i - 1], ligneArray[i], color.plain);
                        }
                        if (techElement.way === false && i === 1) {

                            this.drawDashedLine(ligneArray[i - 1], ligneArray[i], color.plain);
                        }
                    }
                }
            }

            var arrowLength = 5;
            if (techElement.way === true) {
                decalRight = new cc.p(ligneArray[ligneArray.length - 1].x + arrowLength, ligneArray[ligneArray.length - 1].y - arrowLength);
                decalLeft = new cc.p(ligneArray[ligneArray.length - 1].x - arrowLength, ligneArray[ligneArray.length - 1].y - arrowLength);

                this.drawSegment(ligneArray[ligneArray.length - 1], decalRight, 2, yellow);
                this.drawSegment(ligneArray[ligneArray.length - 1], decalLeft, 2, yellow);
            } else {
                if (techElement.objectId === SceneConstants.StaircaseType.doubleQuarterTurn) {
                    decalRight = new cc.p(ligneArray[0].x + arrowLength, ligneArray[0].y - arrowLength);
                    decalLeft = new cc.p(ligneArray[0].x - arrowLength, ligneArray[0].y - arrowLength);

                    this.drawSegment(ligneArray[0], decalRight, 2, yellow);
                    this.drawSegment(ligneArray[0], decalLeft, 2, yellow);
                }
                if (techElement.objectId === SceneConstants.StaircaseType.quarterTurn) {
                    if (techElement.way === false) {
                        decalRight = new cc.p(ligneArray[0].x + arrowLength, ligneArray[0].y + arrowLength);
                        decalLeft = new cc.p(ligneArray[0].x + arrowLength, ligneArray[0].y - arrowLength);
                    }
                    else {
                        decalRight = new cc.p(ligneArray[0].x - arrowLength, ligneArray[0].y + arrowLength);
                        decalLeft = new cc.p(ligneArray[0].x - arrowLength, ligneArray[0].y - arrowLength);
                    }

                    this.drawSegment(ligneArray[0], decalRight, 2, yellow);
                    this.drawSegment(ligneArray[0], decalLeft, 2, yellow);
                }
            }
        }
    }

    dispose() {
    }

    setValidity(value: boolean) {
        this.isValid = value;
    }

    update(dt: any) {
        super.update(dt);
        this.setPosition(GameObjectManager.convertToCocosCoord(this.techElem.transform.localPosition));
        this.setRotationZ(this.techElem.transform.localZRotation);
        this.updateModel(this.techElem, this.techElem.isValid(PlanManager.getInstance().world.currentScene!.currentFloor));
    }

    toResize(pos: math.vec3): boolean {
        return this.resizeMenu.toResize(pos);
    }

    setRotationZ(value: number) {
        this.setRotation(-(value * 360) / (Math.PI * 2.0));
    }

    removeListeners() {
        eventsManager.instance.removeListener(this.hideStairAxo);
    }

    onExit() {
        this.removeListeners();
    }
}

